import {useSignal, useSignals} from '@preact/signals-react/runtime'
import {useQueryClient} from '@tanstack/react-query'
import {Button} from 'primereact/button'
import {Dialog} from 'primereact/dialog'
import {Divider} from 'primereact/divider'
import {InputSwitch} from 'primereact/inputswitch'
import {MultiSelect} from 'primereact/multiselect'
import {Tag} from 'primereact/tag'
import React, {forwardRef, useEffect, useImperativeHandle, useMemo} from 'react'
import {v4 as uuidv4} from 'uuid'
import {HTTP_STATUS_OK, SystemRole} from '../../Constants'
import {ReactComponent as FaDoctor} from '../../assets/icons/FaDoctor.svg'
import {useAppConfig} from '../../context/app/AppContext'
import {useUserHospital} from '../../context/hospital/UserHospitalContext'
import TranslateUtils from '../../i18n/TranslateUtils'
import {QueryKeyTypes} from '../../query-manager/QueryKeyTypes'
import {QueryKeyManager} from '../../query-manager/QueryKeysManager'
import {
    blockOverflownScroll,
    unblockOverflownScroll,
} from '../../utils/BlockScrollsUtils'
import {useAuth} from '../authentication/AuthProvider'
import {getClientId} from '../authentication/AuthUtils'
import {useListUserWithRoles} from '../healthcare/query-keys/HealthCareQueryManager'
import {severityNotification} from '../notification/NotificationConstant'
import {showNotification} from '../notification/NotificationService'
import {isArrayAndNotEmpty} from '../observations/Utils'
import {UserPropertyType} from '../user-property/UserPropertyConstants'
import {
    useAssignPatient,
    useListUserManagement,
} from '../user-property/query-keys/UserPropertyQueryManager'
import {useDatatableContext} from './DataTableContext'
import {
    assignmentEvent,
    assignmentEventTypes,
    getAssignmentEventKey,
    removeToggleAssignmentFormListener,
} from './broadcast/AssignmentEvent'
import {
    getHealthCareEventKey,
    healthCareEvent,
    healthCareEventTypes,
    onHealthCareUserManagementChangeRemoveListener,
} from './hc-practitioner/broadcast/HealthCarePractitionerEvent'

const InitialHCPFilterRequest = {}
const InitialOwnedHCPFilterRequest = {
    mainRefNotNull: true,
}
const StyledAssignmentHealthCarePractitioner = forwardRef(
    ({userIds = []}, ref) => {
        useSignals()
        useImperativeHandle(ref, () => ({
            showForm: handleShowForm,
            hideForm: handleHideForm,
        }))
        //#region Translate
        const {translateCommons, translateDatatable, translateOptions} =
            TranslateUtils()
        //#endregion

        //#region Context
        const {user} = useAuth()
        const {currentHospital} = useUserHospital()
        const {userRole} = useAppConfig()
        const {tableType} = useDatatableContext()
        //#endregion

        //#region State
        const visible = useSignal(false)
        const panelVisible = useSignal(false)
        const selectedItems = useSignal([])
        const override = useSignal(false)

        const handleChangeSelectedItems = e => {
            selectedItems.value = e.value
        }
        //#endregion

        //#region Query
        const queryClient = useQueryClient()
        const {data: hcpOptions} = useListUserWithRoles(
            user?.id,
            true,
            null,
            {
                ...InitialHCPFilterRequest,
            },
            !!user?.id,
        )

        const {data: ownedHCPs, refetch} = useListUserManagement(
            user?.id,
            false,
            {
                ...InitialOwnedHCPFilterRequest,
                subRefId: userIds && userIds.length === 1 ? userIds[0] : null,
            },
            !!user?.id && !!userIds && userIds.length === 1,
        )

        const {mutateAsync: assignPatientToHCP} = useAssignPatient(
            UserPropertyType.userManagement,
            user?.id,
        )
        //#endregion

        //#region Memo
        const ownHCPIds = useMemo(() => {
            if (
                !ownedHCPs ||
                ownedHCPs?.length < 1 ||
                (!!userIds && userIds.length > 1)
            ) {
                return []
            }

            return ownedHCPs?.map(x => x.mainRefId)
        }, [ownedHCPs, userIds])
        //#endregion

        //#region Component State
        const handleShowForm = () => {
            blockOverflownScroll()
            visible.value = true

            if (ownHCPIds?.length > 0 && userIds?.length === 1) {
                selectedItems.value = [...ownHCPIds]
            }
        }

        const handleHideForm = () => {
            unblockOverflownScroll()
            visible.value = false

            selectedItems.value = []
            override.value = false
        }

        const handleOnSubmit = async () => {
            if (!userIds || userIds?.length < 1) {
                showNotification(
                    severityNotification.warn,
                    'Please select the patients you want to assign to the Health Care Practitioner.',
                )

                return
            }

            if (
                !override.value &&
                (!selectedItems.value || selectedItems.value?.length < 1)
            ) {
                showNotification(
                    severityNotification.warn,
                    'Please select at least one Health Care Practitioner',
                )

                return
            }

            let toPostData = {
                userIds: userIds || [],
                hcpIds: selectedItems.value || [],
                override: userIds?.length > 1 ? override.value : true,
            }

            const responseStatus = await assignPatientToHCP(toPostData)
            if (responseStatus && responseStatus === HTTP_STATUS_OK) {
                const {patientKeys} = QueryKeyManager(
                    QueryKeyTypes.patient,
                    user?.id,
                )

                queryClient.invalidateQueries({
                    queryKey: patientKeys.pattern(),
                })

                if (userIds?.length === 1) {
                    refetch()
                }

                handleHideForm()
            }
        }
        //#endregion

        //#region Render & Template
        const panelVisibilityHandler = () => ({
            optionLabel: 'label',
            optionValue: 'value',
            emptyFilterMessage: translateCommons('No results found'),
            resetFilterOnHide: true,
            filter: true,
            filterBy: 'label',
            filterPlaceholder: translateCommons('Search here'),
            showClear: true,
            onShow: () => (panelVisible.value = true),
            onHide: () => (panelVisible.value = false),
        })

        const panelVisiblilityProps = panelVisibilityHandler()

        const renderPanelFooterTemplate = selectedData => {
            let shouldRenderItemSelected = false
            let itemSelectedLength = 0

            if (!!selectedData && typeof selectedData === 'object') {
                if (isArrayAndNotEmpty(selectedData)) {
                    shouldRenderItemSelected = true

                    itemSelectedLength = selectedData?.length
                }

                if (
                    !shouldRenderItemSelected &&
                    Object.keys(selectedData)?.length > 0
                ) {
                    shouldRenderItemSelected = true

                    itemSelectedLength = Object.keys(selectedData)?.length
                }
            }

            return (
                <div className="p-3" style={{backgroundColor: '#f9f9f9'}}>
                    <b>
                        {shouldRenderItemSelected && itemSelectedLength > 0
                            ? `${itemSelectedLength} ${
                                  itemSelectedLength > 1 ? 'items' : 'item'
                              } selected`
                            : `No item selected`}
                    </b>
                </div>
            )
        }
        //#endregion

        //#region Event Lister for Toggle
        useEffect(() => {
            // HCP'S TABLE CHANGE
            healthCareEvent.on(
                getHealthCareEventKey(healthCareEventTypes.TABLE_CHANGE),
                data => {
                    if (
                        !data ||
                        !data?.clientId ||
                        data?.clientId !== getClientId()
                    ) {
                        return
                    }

                    if (
                        userRole === SystemRole.USER_WITH_ROLE &&
                        (!data?.hospitalIds ||
                            (data?.hospitalIds?.length &&
                                !data?.hospitalIds?.includes(
                                    currentHospital?.id,
                                )))
                    ) {
                        return
                    }

                    const {healthCareKeys} = QueryKeyManager(
                        QueryKeyTypes.hcp,
                        user?.id,
                    )

                    queryClient.invalidateQueries({
                        queryKey: healthCareKeys.pattern(),
                    })
                },
            )

            // HCP'S MANAGEMENT CHANGE (Patient Assigned)
            healthCareEvent.on(
                getHealthCareEventKey(
                    healthCareEventTypes.USER_MANAGEMENT_CHANGE,
                ),
                data => {
                    if (
                        !data ||
                        !data?.clientId ||
                        data?.clientId !== getClientId()
                    ) {
                        return
                    }

                    // If the patient selected list contains a user IDs that has been assigned.
                    if (
                        !data?.subRefIds ||
                        !data?.subRefIds?.length ||
                        (userIds &&
                            userIds.length === 1 &&
                            !data?.subRefIds.includes(userIds[0]))
                    ) {
                        return
                    }

                    const {userPropertyKeys} = QueryKeyManager(
                        QueryKeyTypes.userProperty,
                        user?.id,
                    )

                    queryClient.invalidateQueries({
                        queryKey: userPropertyKeys.pattern({
                            type: UserPropertyType.userManagement,
                        }),
                    })
                },
            )

            // ASSIGNMENT FORM
            assignmentEvent.on(
                `${getAssignmentEventKey(
                    tableType,
                    assignmentEventTypes.ON_TOGGLE_HCP,
                )}`,
                () => {
                    handleShowForm()
                },
            )

            return () => {
                onHealthCareUserManagementChangeRemoveListener()
                removeToggleAssignmentFormListener(
                    tableType,
                    assignmentEventTypes.ON_TOGGLE_HCP,
                )
            }
        }, [])
        //#endregion

        return (
            <Dialog
                header={
                    <h5 className="mb-0 font-bold">
                        <strong>
                            {translateCommons(
                                'Assign Patients to HC.Practitioner',
                            )}
                        </strong>
                    </h5>
                }
                visible={visible.value}
                onHide={handleHideForm}
                blockScroll={true}
                style={{width: '50vw'}}
                breakpoints={{'960px': '75vw', '641px': '100vw'}}>
                <div className="w-full">
                    <div className="mb-2">
                        <h4 className="font-bold mb-1 underline-60">
                            {translateCommons('Health Care Practitioner')}
                        </h4>
                    </div>
                    <p>
                        Select one or more Health Care Practitioners to assign
                        the selected patients{` `}
                        <span className="font-bold">
                            ({userIds?.length} patient(s))
                        </span>
                        .
                    </p>

                    <div className="col-12 mb-3">
                        {userIds?.length > 1 ? (
                            <div className="flex align-items-center justify-content-between py-2 mb-3">
                                <div>
                                    <h6 className="font-bold">
                                        {translateDatatable(
                                            'Override Existing Assignment',
                                        )}
                                    </h6>
                                    <p>
                                        {translateCommons(
                                            'This option enables you whether to override the existing assignment or not',
                                        )}
                                    </p>
                                    <p>
                                        <i className="pi pi-info-circle mr-2"></i>
                                        <span className="font-bold">
                                            {override.value ? (
                                                <span>
                                                    With override option
                                                    enabled,{' '}
                                                    <span
                                                        style={{color: 'red'}}>
                                                        any existing assignment
                                                        will be deleted
                                                    </span>{' '}
                                                    and the selected patients
                                                    will be assigned to the
                                                    selected Health Care
                                                    Practitioner(s) regardless
                                                    of their existing
                                                    assignment.
                                                </span>
                                            ) : (
                                                <span>
                                                    With override option
                                                    disabled,{' '}
                                                    <span className="text-darkcyan">
                                                        any existing assignment
                                                        will be kept
                                                    </span>{' '}
                                                    and the selected patients
                                                    will be assigned to the
                                                    selected Health Care
                                                    Practitioner(s) only if they
                                                    are not already assigned to
                                                    them.
                                                </span>
                                            )}
                                        </span>
                                    </p>
                                </div>
                                <div>
                                    <InputSwitch
                                        checked={override.value}
                                        onChange={() => {
                                            override.value = !override.value
                                        }}
                                    />
                                </div>
                            </div>
                        ) : null}

                        <div className="flex align-items-center justify-content-between py-2">
                            <h6 className="font-bold">
                                {translateDatatable(
                                    'Health Care Practitioners',
                                )}
                            </h6>
                        </div>
                        <MultiSelect
                            className="w-full h-3rem p-inputtext p-0 font-bold"
                            options={
                                isArrayAndNotEmpty(hcpOptions)
                                    ? translateOptions(hcpOptions)
                                    : []
                            }
                            placeholder={translateCommons('Select an option')}
                            defaultValue={[]}
                            maxSelectedLabels={3}
                            display={
                                selectedItems.value?.length < 4
                                    ? 'chip'
                                    : 'comma'
                            }
                            value={selectedItems.value || []}
                            onChange={handleChangeSelectedItems}
                            itemTemplate={item => {
                                return (
                                    <div className="w-full flex align-items-center justify-content-between hover:text-primary">
                                        <div className="flex align-items-center mr-5">
                                            <div className="mx-2 mr-2">
                                                {item?.extraData ? (
                                                    <img
                                                        src={item?.extraData}
                                                        alt={item.label}
                                                        width={22}
                                                        height={22}
                                                    />
                                                ) : (
                                                    <FaDoctor
                                                        width={20}
                                                        height={20}
                                                    />
                                                )}
                                            </div>
                                            <div>
                                                <h6 className="font-bold m-0 mb-1">
                                                    {item.label}
                                                </h6>
                                                <small>{item?.data}</small>
                                            </div>
                                        </div>
                                        <div>
                                            <Tag
                                                severity={
                                                    item?.qualification
                                                        ? 'success'
                                                        : 'warning'
                                                }
                                                value={
                                                    item?.qualification
                                                        ? 'Qualified'
                                                        : 'Not Qualified'
                                                }
                                                className="px-3"
                                            />
                                        </div>
                                    </div>
                                )
                            }}
                            panelFooterTemplate={() =>
                                renderPanelFooterTemplate(selectedItems.value)
                            }
                            {...panelVisiblilityProps}
                        />
                        <p className="font-bold">
                            You have selected{' '}
                            <span className="text-darkcyan">
                                {selectedItems.value?.length < 1
                                    ? 'no'
                                    : selectedItems.value?.length}{' '}
                                health care practitioner(s)
                            </span>{' '}
                            out of{' '}
                            <span className="text-darkcyan">
                                {hcpOptions?.length} available health care
                                practitioner(s)
                            </span>
                        </p>

                        <Divider align="center" layout="horizontal" />
                        <h6 className="font-bold mb-2">
                            {translateCommons('Note')}
                        </h6>
                        <ul>
                            <li className="mb-2">
                                You can assign the selected patients to multiple
                                HC.Practitioners
                            </li>
                            <li className="mb-2">
                                If the patient doesn't belong to the hospital of
                                the HC.Practitioner, the action will be ignored.
                            </li>
                            <li className="mb-2">
                                If you want to remove the patient from the HCP,
                                please select only that patient and change the
                                HCP assignment.
                            </li>
                        </ul>

                        {selectedItems.value?.length > 0 && (
                            <React.Fragment>
                                <Divider align="center" layout="horizontal" />
                                <h6 className="font-bold">
                                    {translateCommons('Summary')}
                                </h6>
                                <p>
                                    {' '}
                                    You are about to assign the selected
                                    patients to the following health care
                                    practitioner(s):
                                </p>
                                <ul>
                                    {selectedItems.value?.length > 0 ? (
                                        <React.Fragment>
                                            {selectedItems.value?.map(
                                                (selectedHcp, index) => {
                                                    if (index > 3) {
                                                        return null
                                                    }

                                                    const hcp =
                                                        hcpOptions?.find(
                                                            x =>
                                                                x.value ===
                                                                selectedHcp,
                                                        )

                                                    if (!hcp) {
                                                        return null
                                                    }

                                                    return (
                                                        <li
                                                            key={`${uuidv4()}`}
                                                            className="mb-2">
                                                            <div className="flex align-items-center hover:text-primary">
                                                                <div className="mx-2">
                                                                    {hcp?.extraData ? (
                                                                        <img
                                                                            src={
                                                                                hcp?.extraData
                                                                            }
                                                                            alt={
                                                                                hcp.label
                                                                            }
                                                                            width={
                                                                                22
                                                                            }
                                                                            height={
                                                                                22
                                                                            }
                                                                        />
                                                                    ) : (
                                                                        <FaDoctor
                                                                            width={
                                                                                20
                                                                            }
                                                                            height={
                                                                                20
                                                                            }
                                                                        />
                                                                    )}
                                                                </div>
                                                                <div className="font-bold">
                                                                    {hcp.label}
                                                                </div>
                                                            </div>
                                                        </li>
                                                    )
                                                },
                                            )}

                                            {selectedItems.value?.length > 4 ? (
                                                <li className="mb-2 py-2">
                                                    <span className="font-bold">
                                                        +{' '}
                                                        {selectedItems.value
                                                            ?.length - 4}{' '}
                                                        more health care
                                                        practitioner(s)
                                                    </span>
                                                </li>
                                            ) : null}
                                        </React.Fragment>
                                    ) : null}
                                </ul>
                            </React.Fragment>
                        )}
                    </div>
                    <Button
                        label={translateCommons('SUBMIT')}
                        icon="pi pi-send"
                        onClick={handleOnSubmit}
                        autoFocus
                        className="col-12 h-3rem mb-3"
                        disabled={
                            selectedItems.value?.length < 1 && !override.value
                        }
                    />
                </div>
            </Dialog>
        )
    },
)

export default StyledAssignmentHealthCarePractitioner
